import React, { useState, useMemo } from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@shadcn-in-nx/ui/select";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

function TagsField({ fieldKey, value, placeholder, onChange }) {
    const tags = Array.isArray(value) ? value : [];
    const [inputValue, setInputValue] = React.useState("");

    const addTag = () => {
        const trimmed = inputValue.trim();
        if (trimmed && !tags.includes(trimmed)) {
            onChange(fieldKey, [...tags, trimmed]);
            setInputValue("");
        }
    };

    const removeTag = (index) => {
        onChange(fieldKey, tags.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-2">
            <div className="flex gap-2">
                <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                    placeholder={placeholder}
                    className="flex-1"
                />
                <Button type="button" variant="outline" size="sm" onClick={addTag} className="shrink-0">+</Button>
            </div>
            {tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                    {tags.map((tag, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                            {tag}
                            <button type="button" onClick={() => removeTag(i)} className="hover:text-red-500">&times;</button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

function JsonField({ fieldKey, value, placeholder, onChange }) {
    const [text, setText] = React.useState(() =>
        value && typeof value === "object" ? JSON.stringify(value, null, 2) : (value || "")
    );
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (value && typeof value === "object") {
            setText(JSON.stringify(value, null, 2));
        }
    }, [fieldKey]);

    const handleChange = (e) => {
        const raw = e.target.value;
        setText(raw);
        setError(null);
        if (!raw.trim()) { onChange(fieldKey, {}); return; }
        try {
            onChange(fieldKey, JSON.parse(raw));
        } catch {
            setError("Invalid JSON");
        }
    };

    return (
        <div>
            <textarea
                id={`config_${fieldKey}`}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={placeholder}
                value={text}
                onChange={handleChange}
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
    );
}

function ConfigField({ fieldKey, fieldConfig, value, onChange }) {
    const currentValue = value ?? "";

    // Enum field - show dropdown with options
    if (fieldConfig.enum && fieldConfig.enum.length > 0) {
        return (
            <Select
                value={currentValue.toString()}
                onValueChange={(v) => onChange(fieldKey, v)}
            >
                <SelectTrigger>
                    <SelectValue placeholder={fieldConfig.placeholder} />
                </SelectTrigger>
                <SelectContent>
                    {fieldConfig.enum.map((option) => (
                        <SelectItem key={String(option)} value={String(option)}>
                            {String(option)}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }

    if (fieldConfig.type === "boolean") {
        return (
            <Select
                value={currentValue.toString()}
                onValueChange={(v) => onChange(fieldKey, v === "true")}
            >
                <SelectTrigger>
                    <SelectValue placeholder={fieldConfig.placeholder} />
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="true">True</SelectItem>
                    <SelectItem value="false">False</SelectItem>
                </SelectContent>
            </Select>
        );
    }

    // Date field (format: "date" or type: "date")
    if (fieldConfig.format === "date" || fieldConfig.type === "date") {
        return (
            <Input
                id={`config_${fieldKey}`}
                type="date"
                placeholder={fieldConfig.placeholder}
                value={currentValue ? currentValue.split("T")[0] : ""}
                onChange={(e) => onChange(fieldKey, e.target.value)}
                required={fieldConfig.required}
            />
        );
    }

    // Date-time field (format: "date-time", type: "date-time"/"datetime", or name ends with _at)
    if (fieldConfig.format === "date-time" || fieldConfig.type === "date-time" || fieldConfig.type === "datetime" ||
        /_at$/i.test(fieldKey)) {
        return (
            <Input
                id={`config_${fieldKey}`}
                type="datetime-local"
                placeholder={fieldConfig.placeholder}
                value={currentValue ? currentValue.slice(0, 16) : ""}
                onChange={(e) => onChange(fieldKey, e.target.value ? new Date(e.target.value).toISOString() : "")}
                required={fieldConfig.required}
            />
        );
    }

    // Email field (format or name-based)
    if (fieldConfig.format === "email" || /^email$|_email$/i.test(fieldKey)) {
        return (
            <Input
                id={`config_${fieldKey}`}
                type="email"
                placeholder={fieldConfig.placeholder}
                value={currentValue}
                onChange={(e) => onChange(fieldKey, e.target.value)}
                required={fieldConfig.required}
            />
        );
    }

    // Phone field (name-based)
    if (/phone|mobile|tel|whatsapp/i.test(fieldKey)) {
        return (
            <Input
                id={`config_${fieldKey}`}
                type="tel"
                placeholder={fieldConfig.placeholder || "+1 (555) 000-0000"}
                value={currentValue}
                onChange={(e) => onChange(fieldKey, e.target.value)}
                required={fieldConfig.required}
            />
        );
    }

    // URL field (format: "uri"/"url" or field name contains url/link/website)
    if (fieldConfig.format === "uri" || fieldConfig.format === "url" ||
        /_(url|link|website|href)$|^(url|link|website|href)$|_url_/i.test(fieldKey)) {
        return (
            <Input
                id={`config_${fieldKey}`}
                type="url"
                placeholder={fieldConfig.placeholder || "https://..."}
                value={currentValue}
                onChange={(e) => onChange(fieldKey, e.target.value)}
                required={fieldConfig.required}
            />
        );
    }

    if (fieldConfig.type === "number" || fieldConfig.type === "integer") {
        return (
            <Input
                id={`config_${fieldKey}`}
                type="number"
                placeholder={fieldConfig.placeholder}
                value={currentValue}
                onChange={(e) =>
                    onChange(fieldKey, parseFloat(e.target.value) || "")
                }
                required={fieldConfig.required}
            />
        );
    }

    // Array of strings (tags)
    if (fieldConfig.type === "array") {
        return (
            <TagsField
                fieldKey={fieldKey}
                value={value}
                placeholder={fieldConfig.placeholder}
                onChange={onChange}
            />
        );
    }

    // Object (JSON editor)
    if (fieldConfig.type === "object") {
        return (
            <JsonField
                fieldKey={fieldKey}
                value={value}
                placeholder={fieldConfig.placeholder}
                onChange={onChange}
            />
        );
    }

    if (fieldConfig.type === "string" && fieldConfig.maxLength > 100) {
        return (
            <textarea
                id={`config_${fieldKey}`}
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={fieldConfig.placeholder}
                value={currentValue}
                onChange={(e) => onChange(fieldKey, e.target.value)}
                maxLength={fieldConfig.maxLength}
                required={fieldConfig.required}
            />
        );
    }

    return (
        <Input
            id={`config_${fieldKey}`}
            type="text"
            placeholder={fieldConfig.placeholder}
            value={currentValue}
            onChange={(e) => onChange(fieldKey, e.target.value)}
            maxLength={fieldConfig.maxLength}
            required={fieldConfig.required}
        />
    );
}

const BUILT_IN_FIELDS = [
    "id", "createdAt", "updatedAt", "createdBy",
    "username", "email", "password", "firstName",
    "lastName", "isActive",
];

export default function UserFormDialog({
    open,
    onOpenChange,
    editingUser,
    userEntitySchema,
    onSubmit,
    formError,
}) {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        username: "",
        email: "",
        password: "",
        firstName: "",
        lastName: "",
        role: "user",
        isActive: true,
        config: {},
    });

    const configKeys = useMemo(() => {
        if (!userEntitySchema?.properties) return [];
        return Object.keys(userEntitySchema.properties).filter(
            (key) => !BUILT_IN_FIELDS.includes(key)
        );
    }, [userEntitySchema]);

    const getFieldConfig = (fieldKey) => {
        const field = userEntitySchema?.properties?.[fieldKey];
        if (!field) return { label: fieldKey, type: "text", placeholder: fieldKey, required: false };
        return {
            label: fieldKey,
            type: field.type || "text",
            placeholder: field.description || fieldKey,
            required: userEntitySchema.required?.includes(fieldKey) || false,
            maxLength: field.maxLength,
            format: field.format,
            default: field.default,
            enum: field.enum || null,
            items: field.items || null,
        };
    };

    // Reset form when dialog opens
    React.useEffect(() => {
        if (open) {
            if (editingUser) {
                setFormData({
                    username: editingUser.username,
                    email: editingUser.email,
                    password: "",
                    firstName: editingUser.firstName,
                    lastName: editingUser.lastName,
                    role: editingUser.roles?.[0] || "user",
                    isActive: editingUser.isActive,
                    config: editingUser.config || {},
                });
            } else {
                setFormData({
                    username: "", email: "", password: "",
                    firstName: "", lastName: "", role: "user",
                    isActive: true, config: {},
                });
            }
        }
    }, [open, editingUser]);

    const handleConfigChange = (key, value) => {
        const fieldConfig = getFieldConfig(key);
        let processedValue = value;
        if (fieldConfig.type === "boolean") processedValue = Boolean(value);
        else if (fieldConfig.type === "number" || fieldConfig.type === "integer")
            processedValue = value === "" ? "" : Number(value);

        setFormData((prev) => ({
            ...prev,
            config: { ...prev.config, [key]: processedValue },
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData, editingUser);
    };

    const setField = (field, value) =>
        setFormData((prev) => ({ ...prev, [field]: value }));

    const passwordInvalid = formData.password &&
        (formData.password.length < 7 || !/[A-Z]/.test(formData.password) || !/[0-9]/.test(formData.password));

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                <DialogHeader className="flex-shrink-0">
                    <DialogTitle>
                        {editingUser ? t("workflowUsers.editUser") : t("workflowUsers.createNewUser")}
                    </DialogTitle>
                </DialogHeader>
                {formError && (
                    <div className="flex items-center gap-2 p-3 text-sm text-red-600 bg-red-50 rounded-md flex-shrink-0">
                        <AlertCircle className="w-4 h-4" />
                        {formError}
                    </div>
                )}
                <div className="flex-1 overflow-y-auto pr-2">
                    <form id="user-form" onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="username">{t("workflowUsers.username")}</Label>
                                <Input id="username" value={formData.username} onChange={(e) => setField("username", e.target.value)} required />
                            </div>
                            <div>
                                <Label htmlFor="email">{t("workflowUsers.email")}</Label>
                                <Input id="email" type="email" value={formData.email} onChange={(e) => setField("email", e.target.value)} required />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="firstName">{t("workflowUsers.firstName")}</Label>
                                <Input id="firstName" value={formData.firstName} onChange={(e) => setField("firstName", e.target.value)} required />
                            </div>
                            <div>
                                <Label htmlFor="lastName">{t("workflowUsers.lastName")}</Label>
                                <Input id="lastName" value={formData.lastName} onChange={(e) => setField("lastName", e.target.value)} required />
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="password">
                                {t("workflowUsers.password")}{" "}
                                {editingUser && `(${t("workflowUsers.passwordPlaceholder")})`}
                            </Label>
                            <Input id="password" type="password" value={formData.password} onChange={(e) => setField("password", e.target.value)} required={!editingUser} minLength={7} />
                            {passwordInvalid && (
                                <p className="text-sm text-red-600 mt-1">{t("workflowUsers.passwordRequirements")}</p>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="role">{t("workflowUsers.role")}</Label>
                                <Select value={formData.role} onValueChange={(v) => setField("role", v)}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="user">{t("workflowUsers.roleUser")}</SelectItem>
                                        <SelectItem value="admin">{t("workflowUsers.roleAdmin")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="isActive">{t("workflowUsers.statusLabel")}</Label>
                                <Select value={formData.isActive.toString()} onValueChange={(v) => setField("isActive", v === "true")}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="true">{t("workflowUsers.active")}</SelectItem>
                                        <SelectItem value="false">{t("workflowUsers.inactive")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        {configKeys.length > 0 && (
                            <div className="space-y-2 mt-2">
                                {configKeys.map((fieldKey) => {
                                    const fc = getFieldConfig(fieldKey);
                                    return (
                                        <div key={fieldKey}>
                                            <Label htmlFor={`config_${fieldKey}`}>
                                                {fc.label}
                                                {fc.required && <span className="text-red-500 ml-1">*</span>}
                                            </Label>
                                            <ConfigField
                                                fieldKey={fieldKey}
                                                fieldConfig={fc}
                                                value={formData.config[fieldKey]}
                                                onChange={handleConfigChange}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </form>
                </div>
                <div className="flex justify-end gap-2 pt-4 flex-shrink-0 border-t">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        {t("workflowUsers.cancel")}
                    </Button>
                    <Button type="submit" form="user-form">
                        {editingUser ? t("workflowUsers.updateUser") : t("workflowUsers.createUser")}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
